import { useMutation } from '@apollo/client/react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@optools/components/ui/alert-dialog';
import { Button } from '@optools/components/ui/button';
import { DELETE_PLAYBACK_OVERRIDE } from '@optools/services/operator-gateway/overrides/playback/mutations';
import { GET_PLAYBACK_OVERRIDES } from '@optools/services/operator-gateway/overrides/playback/queries';
import { Trash } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export interface DeletePlaybackOverrideProps {
  overrideId: string | undefined;
}

export function DeletePlaybackOverride({ overrideId }: DeletePlaybackOverrideProps) {
  const [open, setOpen] = useState(false);
  const [deletePlaybackOverride, { loading }] = useMutation(DELETE_PLAYBACK_OVERRIDE, {
    awaitRefetchQueries: true,
    onCompleted: async ({ deletePlaybackOverride }) => {
      if (!deletePlaybackOverride) {
        toast.error('Delete Override Error', {
          description: 'Failed to delete the override, please try again',
          duration: Infinity,
        });
      } else {
        toast.success('Playback Override successfully deleted');

        setOpen(false);
      }
    },
    refetchQueries: [{ query: GET_PLAYBACK_OVERRIDES }],
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (loading) return;
    setOpen(nextOpen);
  };

  const handleDelete = () => {
    if (!overrideId) return;

    deletePlaybackOverride({ variables: { overrideId: +overrideId } });
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button size="icon" title="Delete Override" variant="destructive">
          <Trash />
          <span className="sr-only">Delete Override</span>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Override</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this override?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button disabled={loading} loading={loading} variant="destructive" onClick={handleDelete}>
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
